import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  CloseSmIcon,
  IconButton,
} from '@crono-one/design-system';
import { cn } from 'utils/style-utils';

interface IProspectInfoFieldProps<T> {
  label: string;
  value: string | null | undefined;
  selectedValueSlot: React.ReactNode;
  onChange: (value: string | null | number) => void;
  id: string;
  options: T[];
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string | number;
  searchText: string;
  onSearchTextChange: (text: string) => void;
  placeholder: string;
  hideRemoveButton?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  invalid?: boolean;
  disabled?: boolean;
  noOptionsText?: string;
}

export function ProspectInfoField<T>({
  label,
  value,
  selectedValueSlot,
  id,
  options,
  getOptionLabel,
  getOptionValue,
  onChange,
  searchText,
  onSearchTextChange,
  placeholder,
  hideRemoveButton,
  invalid,
  disabled,
  noOptionsText,
}: IProspectInfoFieldProps<T>) {
  const items = options.map((option) => ({
    value: getOptionValue(option),
    label: getOptionLabel(option),
  }));

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-semibold text-gray-900" htmlFor={id}>
        {label}
      </label>

      {value ? (
        <div
          className={cn(
            'w-full h-8 min-h-8 flex items-center justify-between gap-2 pl-1 pr-2 rounded-lg border border-neutral-400',
            { 'opacity-50 pointer-events-none': disabled },
          )}
        >
          {selectedValueSlot}
          {!hideRemoveButton && (
            <IconButton
              label={`Clear ${label}`}
              icon={CloseSmIcon}
              variant="text"
              size="sm"
              disabled={disabled}
              onClick={() => {
                onChange(null);
              }}
            />
          )}
        </div>
      ) : (
        <Autocomplete
          items={items}
          disabled={disabled}
          value={searchText}
          onValueChange={(text, details) => {
            onSearchTextChange(text);
            if (!text) {
              onChange(null);
              return;
            }
            // Only select on an explicit pick (click / Enter on an item), never
            // while typing — otherwise typing a string that happens to equal a
            // result's label auto-selects it.
            if (details.reason !== 'item-press') {
              return;
            }
            const match = options.find(
              (option) => getOptionLabel(option) === text,
            );
            if (match) {
              onChange(getOptionValue(match));
            }
          }}
        >
          <AutocompleteInput
            id={id}
            placeholder={placeholder}
            dsSize="sm"
            showClear
            aria-invalid={invalid}
            disabled={disabled}
          />

          <AutocompleteContent positionerClassname="z-50000">
            <AutocompleteEmpty>{noOptionsText}</AutocompleteEmpty>
            <AutocompleteList>
              {(item) => (
                <AutocompleteItem key={item.value} value={item}>
                  {item.label}
                </AutocompleteItem>
              )}
            </AutocompleteList>
          </AutocompleteContent>
        </Autocomplete>
      )}
    </div>
  );
}
